import React from 'react';
import KpiGrid from './KpiGrid';
import SparklineBlock from './SparklineBlock';
import RankingTable from './RankingTable';
import BarChartBlock from './BarChartBlock';
import LineAreaBlock from './LineAreaBlock';
import CategoryPie from './CategoryPie';
import ComparisonCards from './ComparisonCards';
import AnomalyList from './AnomalyList';
import RiskTierCards from './RiskTierCards';
import './chat-blocks.css';

/**
 * Dispatch a typed block payload to the correct presentational component.
 * Unknown types render nothing in production (silent skip) but show a
 * dev-only hint so missing handlers don't go unnoticed during development.
 */
function BlockRenderer({ block, dir = 'ltr', onSuggest }) {
    if (!block || typeof block !== 'object') return null;

    switch (block.type) {
        case 'kpi_grid':
            return <KpiGrid title={block.title} items={block.items} />;
        case 'sparkline':
            return <SparklineBlock title={block.title} series={block.series} labels={block.labels} />;
        case 'ranking_table':
            return (
                <RankingTable
                    title={block.title}
                    rows={block.rows}
                    valueFormat={block.valueFormat}
                    medalled={block.medalled}
                    cols={block.cols}
                    drillTemplate={block.drillTemplate}
                    onSuggest={onSuggest}
                />
            );
        case 'bar_chart':
            return (
                <BarChartBlock
                    title={block.title}
                    data={block.data}
                    valueFormat={block.valueFormat}
                    dir={dir}
                />
            );
        case 'line_chart':
            return (
                <LineAreaBlock
                    title={block.title}
                    data={block.data}
                    valueFormat={block.valueFormat}
                    markers={block.markers}
                    dir={dir}
                />
            );
        case 'category_pie':
            return <CategoryPie title={block.title} items={block.items} dir={dir} />;
        case 'comparison_cards':
            return (
                <ComparisonCards
                    title={block.title}
                    a={block.a}
                    b={block.b}
                    delta_pct={block.delta_pct}
                    winner={block.winner}
                    cols={block.cols}
                />
            );
        case 'anomaly_list':
            return (
                <AnomalyList
                    title={block.title}
                    items={block.items}
                    labels={block.labels}
                    totals={block.totals}
                    onSuggest={onSuggest}
                />
            );
        case 'risk_tier_cards':
            return (
                <RiskTierCards
                    title={block.title}
                    items={block.items}
                    atRiskRevenue={block.atRiskRevenue}
                    labels={block.labels}
                    onSuggest={onSuggest}
                />
            );
        default:
            if (process.env.NODE_ENV !== 'production') {
                return (
                    <div className="chat-block" style={{ opacity: 0.6, fontSize: 11 }}>
                        Unsupported block type: {String(block.type)}
                    </div>
                );
            }
            return null;
    }
}

/**
 * Render the full list of blocks for a message. Wraps in a `dir`-aware
 * container so RTL flips axis direction / column alignment uniformly.
 */
export default function BlockList({ blocks, dir = 'ltr', onSuggest }) {
    if (!Array.isArray(blocks) || !blocks.length) return null;
    return (
        <div className="chat-blocks" dir={dir}>
            {blocks.map((b, i) => (
                <BlockRenderer key={i} block={b} dir={dir} onSuggest={onSuggest} />
            ))}
        </div>
    );
}
